/**
 * What every popup frame on /integrations does (Integrations part 1, PV13, 2026-10-11).
 *
 * Moved verbatim out of src/services/composio/panel/PanelModal.tsx, which draws the frame for the page's dialogs
 * and for the sign-in and workstation windows too: Escape and a press on the backdrop close it unless it is not
 * dismissable; Escape never closes it while the browser is fullscreen, nor for 400 ms after leaving fullscreen
 * (Escape is also the browser's exit-fullscreen gesture); the page is held still while it is open and given back
 * as it was; focus moves into the card when it opens; the card is handed to a caller that asks for it; it opens
 * in the document's body (a portal), so the chat's containing block can never trap it. Its listeners and focus
 * are set up again whenever its handler or dismissability changes. The frame draws; today's and the new design's
 * read the same hook.
 */
import { useCallback, useEffect, useRef, type MouseEvent as ReactMouseEvent, type MutableRefObject } from 'react';

export function usePanelModal({ onClose, dismissable, cardRef: externalCardRef }: {
  onClose: () => void;
  dismissable: boolean;
  cardRef?: MutableRefObject<HTMLDivElement | null>;
}) {
  const cardRef = useRef<HTMLDivElement | null>(null);
  // One stable callback ref: an inline arrow here re-runs (null → node) on
  // every render, which is wasted work and a footgun for any consumer that
  // reacts to the ref changing.
  const mergedRef = useCallback((node: HTMLDivElement | null) => {
    cardRef.current = node;
    if (externalCardRef) externalCardRef.current = node;
  }, [externalCardRef]);
  // Stamped whenever we leave fullscreen — see the Escape guard below.
  const lastFullscreenExitRef = useRef(0);

  useEffect(() => {
    const isFullscreen = () => {
      try {
        return !!(
          document.fullscreenElement
          || (document as unknown as { webkitFullscreenElement?: Element | null })
            .webkitFullscreenElement
        );
      } catch {
        return false;
      }
    };
    const onFullscreenChange = () => {
      if (!isFullscreen()) lastFullscreenExitRef.current = Date.now();
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    document.addEventListener('webkitfullscreenchange', onFullscreenChange as EventListener);

    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || !dismissable) return;
      // ESCAPE IS ALSO THE BROWSER'S EXIT-FULLSCREEN GESTURE (2026-09-04).
      // Whether a keydown is dispatched for the Escape that leaves
      // fullscreen differs by engine — Chrome and Firefox swallow it, others
      // do not. Unguarded, a user pressing Escape to come out of fullscreen
      // instead destroys a half-finished sign-in, closing the journey and
      // the VM session with it. So: never close while fullscreen, and not
      // for a moment afterwards either.
      if (isFullscreen()) return;
      if (Date.now() - lastFullscreenExitRef.current < 400) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    // Keep the page from scrolling underneath the overlay.
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    // Move focus into the dialog so keyboard users land inside it.
    cardRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('fullscreenchange', onFullscreenChange);
      document.removeEventListener('webkitfullscreenchange', onFullscreenChange as EventListener);
      document.body.style.overflow = prevOverflow;
    };
  }, [dismissable, onClose]);

  return {
    mergedRef,
    /** A press on the backdrop itself (never inside the card) closes a dismissable frame. */
    onBackdropMouseDown: (e: ReactMouseEvent<HTMLDivElement>) => {
      if (dismissable && e.target === e.currentTarget) onClose();
    },
    // SSR / test environments without a document render in place rather than
    // throwing: every caller here is client-only, but the guard costs nothing.
    portalTarget: typeof document === 'undefined' ? null : document.body,
  };
}
